import React, { useState, useEffect } from 'react';
import api from '../services/api';

const statusLabel = { aman: 'Aman', waspada: 'Waspada', kritis: 'Kritis', belum: 'Belum Inspeksi' };
const statusColor = { aman: '#00A651', waspada: '#E0A526', kritis: '#D9362B', belum: '#9CA3AF' };
const metricColor = { komposit: '#00A651', apar: '#3B82F6', apd: '#E0A526', p3k: '#8B5CF6' };
const metricLabel = { komposit: 'Komposit', apar: 'APAR', apd: 'APD', p3k: 'P3K' };
const metricField = { komposit: 'nilai_psri', apar: 'nilai_apar', apd: 'nilai_apd', p3k: 'nilai_p3k' };

function LineChartSVG({ data, dataKey, color, height = 220 }) {
  if (!data || data.length === 0) {
    return <div className="text-center text-gray-400 text-sm py-16">Belum ada data historis untuk periode ini</div>;
  }
  const w = 640, h = height, padding = 34;
  const max = 100, min = 0;
  const stepX = data.length > 1 ? (w - padding * 2) / (data.length - 1) : 0;
  const points = data.map((d, i) => {
    const x = data.length > 1 ? padding + i * stepX : w / 2;
    const val = d[dataKey] ?? 0;
    const y = h - padding - ((Math.min(val, max) - min) / (max - min)) * (h - padding * 2);
    return { x, y, val, label: d.label };
  });
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const areaD = points.length > 1
    ? `${pathD} L ${points[points.length - 1].x} ${h - padding} L ${points[0].x} ${h - padding} Z`
    : '';

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ maxHeight: height }}>
      {[0, 25, 50, 75, 100].map(v => {
        const y = h - padding - (v / 100) * (h - padding * 2);
        return (
          <g key={v}>
            <line x1={padding} y1={y} x2={w - padding} y2={y} stroke="#F0F2F1" strokeWidth="1" />
            <text x={padding - 8} y={y + 3} textAnchor="end" fontSize="9" fill="#B0B8B4">{v}</text>
          </g>
        );
      })}
      {points.length > 1 && <path d={areaD} fill={color} fillOpacity="0.10" />}
      {points.length > 1 && <path d={pathD} fill="none" stroke={color} strokeWidth="2.5" />}
      {points.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="4.5" fill={color} stroke="#fff" strokeWidth="2" />
          <text x={p.x} y={p.y - 12} textAnchor="middle" fontSize="10.5" fontWeight="700" fill={color}>{p.val.toFixed(1)}</text>
          <text x={p.x} y={h - 8} textAnchor="middle" fontSize="10" fill="#9CA3AF">{p.label}</text>
        </g>
      ))}
    </svg>
  );
}

function MiniSparkline({ data, color, height = 56 }) {
  if (!data || data.length === 0) return <div className="h-14 flex items-center justify-center text-[10px] text-gray-300">No data</div>;
  const w = 220, h = height, padding = 4;
  const max = 100, min = 0;
  const stepX = data.length > 1 ? (w - padding * 2) / (data.length - 1) : 0;
  const points = data.map((d, i) => {
    const x = data.length > 1 ? padding + i * stepX : w / 2;
    const val = d.nilai_psri ?? 0;
    const y = h - padding - ((Math.min(val, max) - min) / (max - min)) * (h - padding * 2);
    return { x, y };
  });
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ height }}>
      {points.length > 1 && <path d={pathD} fill="none" stroke={color} strokeWidth="2" />}
      {points.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r="2.5" fill={color} />)}
    </svg>
  );
}

export default function Monitoring() {
  const [rollup, setRollup] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState('');
  const [modal, setModal] = useState(null);
  const [activeTab, setActiveTab] = useState('kesiapan');

  const bulanList = ['Januari','Februari','Maret','April','Mei','Juni','Juli','Agustus','September','Oktober','November','Desember'];
  const [bulanLaporan, setBulanLaporan] = useState(new Date().getMonth() + 1);
  const [tahunLaporan, setTahunLaporan] = useState(new Date().getFullYear());

  // ===== State untuk tab Tren Nilai PSRI =====
  const [trenGI, setTrenGI] = useState(null);
  const [trenRange, setTrenRange] = useState(6);
  const [trenMetric, setTrenMetric] = useState('komposit');
  const [trenData, setTrenData] = useState(null);
  const [trenAllGI, setTrenAllGI] = useState([]);
  const [trenLoading, setTrenLoading] = useState(false);

  const bukaLaporanBulanan = () => {
    const url = `${api.defaults.baseURL}/laporan/bulanan-k3l?bulan=${bulanLaporan}&tahun=${tahunLaporan}`;
    const token = localStorage.getItem('psri_token');
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.blob())
      .then(blob => {
        const blobUrl = window.URL.createObjectURL(blob);
        window.open(blobUrl, '_blank');
      })
      .catch(err => console.error('Gagal buka laporan:', err));
  };

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const res = await api.get('/monitoring/rollup');
      const gi = res.data.data.gardu_induk || [];
      setRollup(gi);
      if (!trenGI && gi.length > 0) setTrenGI(gi[0].id);
      setLastUpdate(new Date().toLocaleString('id-ID'));
    } catch (err) {
      console.error('Error:', err);
      setRollup([]);
    }
    setIsLoading(false);
  };

  // ===== Load data tren saat tab dibuka / filter berubah =====
  useEffect(() => {
    if (activeTab === 'tren' && trenGI) {
      loadTren(trenGI, trenRange);
      loadTrenAllGI(trenRange);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, trenGI, trenRange]);

  const loadTren = async (giId, range) => {
    setTrenLoading(true);
    try {
      const res = await api.get(`/monitoring/tren/${giId}?bulan_range=${range}`);
      setTrenData(res.data.data);
    } catch (err) {
      console.error('Error load tren:', err);
      setTrenData(null);
    }
    setTrenLoading(false);
  };

  const loadTrenAllGI = async (range) => {
    try {
      const res = await api.get(`/monitoring/tren-semua-gi?bulan_range=${range}`);
      setTrenAllGI(res.data.data.gardu_induk || []);
    } catch (err) {
      console.error('Error load tren semua GI:', err);
      setTrenAllGI([]);
    }
  };

  const sumCategory = (cat) => {
    const total = { aman: 0, waspada: 0, kritis: 0, belum: 0 };
    rollup.forEach(gi => {
      ['aman', 'waspada', 'kritis', 'belum'].forEach(k => { total[k] += gi[cat][k]; });
    });
    return total;
  };
  const sumAll = () => {
    const total = { aman: 0, waspada: 0, kritis: 0, belum: 0 };
    ['apar', 'apd', 'p3k'].forEach(cat => {
      const c = sumCategory(cat);
      ['aman', 'waspada', 'kritis', 'belum'].forEach(k => { total[k] += c[k]; });
    });
    return total;
  };

  const chartData = { APAR: sumCategory('apar'), APD: sumCategory('apd'), P3K: sumCategory('p3k'), TOTAL: sumAll() };

  const openDrill = async (gi, giNama, kategori, status, count) => {
    if (count === 0) return;
    setModal({ gi, giNama, kategori, status, items: [], loading: true });
    try {
      const res = await api.get(`/monitoring/detail?gi=${gi}&kategori=${kategori}&status=${status}`);
      setModal({ gi, giNama, kategori, status, items: res.data.data.items || [], loading: false });
    } catch (err) {
      console.error(err);
      setModal({ gi, giNama, kategori, status, items: [], loading: false });
    }
  };

  const CountBtn = ({ gi, giNama, kategori, status, count }) => (
    <button
      onClick={() => openDrill(gi, giNama, kategori, status, count)}
      disabled={count === 0}
      className="text-xs font-mono font-bold px-2 py-1 rounded-md transition-transform hover:scale-110 disabled:opacity-30 disabled:hover:scale-100"
      style={{
        backgroundColor: count === 0 ? '#F3F4F6' : `${statusColor[status]}18`,
        color: count === 0 ? '#9CA3AF' : statusColor[status],
        cursor: count === 0 ? 'default' : 'pointer',
      }}
    >
      {count}
    </button>
  );

  if (isLoading) return (
    <div className="flex items-center justify-center h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
        <p className="text-gray-500">Memuat data...</p>
      </div>
    </div>
  );

  // ===== Insight untuk kartu ringkasan tren =====
  const trenSeries = trenData?.tren || [];
  const metricKey = metricField[trenMetric];
  const lastPoint = trenSeries[trenSeries.length - 1];
  const prevPoint = trenSeries[trenSeries.length - 2];
  const delta = lastPoint && prevPoint ? (lastPoint[metricKey] - prevPoint[metricKey]) : null;
  const avgValue = trenSeries.length > 0
    ? (trenSeries.reduce((sum, d) => sum + (d[metricKey] || 0), 0) / trenSeries.length)
    : null;
  const lowestPoint = trenSeries.length > 0
    ? trenSeries.reduce((min, d) => (d[metricKey] < min[metricKey] ? d : min), trenSeries[0])
    : null;

  return (
    <div className="p-4 h-screen flex flex-col overflow-hidden">
      <div className="flex justify-between items-center mb-3 bg-green-50 rounded-xl px-5 py-3 border border-green-100">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Monitoring Kerawanan</h1>
          <p className="text-gray-500 text-xs mt-0.5">
            <span className="text-green-700 font-semibold">&#9679; LIVE</span>
            {' '}&middot; Update: {lastUpdate}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={bukaLaporanBulanan}
            className="bg-white text-green-800 border border-green-300 px-4 py-2 rounded-lg hover:bg-green-50 transition-colors text-sm font-semibold shadow-sm flex items-center gap-1.5">
            &#128190; Laporan Bulanan
          </button>
          <button onClick={loadData} className="bg-green-700 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors text-sm font-semibold shadow-sm">
            &#8635; Refresh
          </button>
        </div>
      </div>

      {/* ===== Tab bar ===== */}
      <div className="flex gap-1 mb-3 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('kesiapan')}
          className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
            activeTab === 'kesiapan' ? 'border-green-600 text-green-800' : 'border-transparent text-gray-500 hover:text-green-700'
          }`}
        >
          Monitor Kesiapan
        </button>
        <button
          onClick={() => setActiveTab('tren')}
          className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
            activeTab === 'tren' ? 'border-green-600 text-green-800' : 'border-transparent text-gray-500 hover:text-green-700'
          }`}
        >
          Tren Nilai PSRI
        </button>
      </div>

      {/* ===== TAB: Monitor Kesiapan (existing, tidak diubah) ===== */}
      {activeTab === 'kesiapan' && (
        <>
          <div className="bg-white rounded-xl border border-gray-100 px-4 py-3 mb-3 flex items-center gap-3 flex-wrap">
            <span className="text-xs font-semibold text-gray-500">Periode laporan:</span>
            <select value={bulanLaporan} onChange={(e) => setBulanLaporan(parseInt(e.target.value))}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-gray-50">
              {bulanList.map((b, i) => <option key={i} value={i + 1}>{b}</option>)}
            </select>
            <select value={tahunLaporan} onChange={(e) => setTahunLaporan(parseInt(e.target.value))}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-gray-50">
              {[2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>

          <div className="flex-1 overflow-y-auto space-y-4">

            <div>
              <h2 className="text-xs font-bold text-green-800 tracking-wide mb-2">GRAFIK AGREGAT</h2>
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <div className="flex gap-4 mb-4 flex-wrap">
                  {Object.entries(statusLabel).map(([key, label]) => (
                    <div key={key} className="flex items-center gap-1.5 text-xs text-gray-500">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: statusColor[key] }}></span>
                      {label}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(chartData).map(([label, vals]) => {
                    const max = Math.max(vals.aman, vals.waspada, vals.kritis, vals.belum, 1);
                    return (
                      <div key={label} className="text-center">
                        <p className="text-xs font-semibold text-gray-700 mb-2">{label}</p>
                        <div className="flex items-end justify-center gap-2 h-32">
                          {['aman', 'waspada', 'kritis', 'belum'].map(k => (
                            <div key={k} className="flex flex-col items-center gap-1 w-7">
                              <span className="text-[10px] font-mono font-bold text-gray-600">{vals[k]}</span>
                              <div
                                className="w-full rounded-t"
                                style={{ height: `${Math.max((vals[k] / max) * 100, vals[k] > 0 ? 4 : 0)}px`, backgroundColor: statusColor[k] }}
                              ></div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-xs font-bold text-green-800 tracking-wide mb-2">MONITOR KESIAPAN GARDU INDUK</h2>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
                <table className="text-xs" style={{ minWidth: '900px', width: '100%' }}>
                  <thead>
                    <tr>
                      <th rowSpan="2" className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase align-bottom bg-gray-50">Gardu Induk</th>
                      <th colSpan="4" className="px-2 py-1.5 text-center text-[10px] font-bold text-gray-700 bg-green-50 border-l border-gray-100">APAR (40%)</th>
                      <th colSpan="4" className="px-2 py-1.5 text-center text-[10px] font-bold text-gray-700 bg-blue-50 border-l border-gray-100">APD (35%)</th>
                      <th colSpan="4" className="px-2 py-1.5 text-center text-[10px] font-bold text-gray-700 bg-yellow-50 border-l border-gray-100">P3K (25%)</th>
                    </tr>
                    <tr>
                      {['apar', 'apd', 'p3k'].map(cat => (
                        ['Aman', 'Waspada', 'Kritis', 'Belum'].map(s => (
                          <th key={cat + s} className="px-2 py-1.5 text-center text-[9px] font-medium text-gray-400 bg-gray-50 border-l border-gray-100">{s}</th>
                        ))
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rollup.map((gi) => (
                      <tr key={gi.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 font-semibold text-gray-800">{gi.nama}</td>
                        {['apar', 'apd', 'p3k'].map(cat => (
                          ['aman', 'waspada', 'kritis', 'belum'].map(s => (
                            <td key={cat + s} className="px-2 py-2 text-center border-l border-gray-50">
                              <CountBtn gi={gi.id} giNama={gi.nama} kategori={cat} status={s} count={gi[cat][s]} />
                            </td>
                          ))
                        ))}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50 font-bold">
                      <td className="px-3 py-2 text-gray-700">TOTAL</td>
                      {['apar', 'apd', 'p3k'].map(cat => {
                        const t = sumCategory(cat);
                        return ['aman', 'waspada', 'kritis', 'belum'].map(s => (
                          <td key={cat + s} className="px-2 py-2 text-center text-gray-700 border-l border-gray-100">{t[s]}</td>
                        ));
                      })}
                    </tr>
                  </tfoot>
                </table>
              </div>
              <p className="text-[11px] text-gray-400 mt-2">* Klik angka pada tabel untuk melihat rincian item</p>
            </div>

          </div>
        </>
      )}

      {/* ===== TAB: Tren Nilai PSRI (BARU) ===== */}
      {activeTab === 'tren' && (
        <div className="flex-1 overflow-y-auto space-y-4">

          <div className="bg-white rounded-xl border border-gray-100 px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap">
              <div>
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Gardu Induk</p>
                <select
                  value={trenGI || ''}
                  onChange={(e) => setTrenGI(parseInt(e.target.value))}
                  className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-gray-50"
                >
                  {rollup.map(gi => <option key={gi.id} value={gi.id}>{gi.nama}</option>)}
                </select>
              </div>
              <div>
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Periode</p>
                <div className="flex bg-gray-100 rounded-lg p-1 gap-1">
                  {[3, 6, 12].map(r => (
                    <button
                      key={r}
                      onClick={() => setTrenRange(r)}
                      className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors ${
                        trenRange === r ? 'bg-white text-green-800 shadow-sm' : 'text-gray-500'
                      }`}
                    >
                      {r} Bulan
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Metrik</p>
              <div className="flex gap-1.5 flex-wrap">
                {Object.keys(metricLabel).map(m => (
                  <button
                    key={m}
                    onClick={() => setTrenMetric(m)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors"
                    style={{
                      borderColor: trenMetric === m ? metricColor[m] : '#E5E7EB',
                      backgroundColor: trenMetric === m ? metricColor[m] : '#fff',
                      color: trenMetric === m ? '#fff' : '#6B7280',
                    }}
                  >
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: trenMetric === m ? '#fff' : metricColor[m] }}></span>
                    {metricLabel[m]}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h2 className="text-xs font-bold text-green-800 tracking-wide mb-3">
              TREN NILAI PSRI &mdash; {trenData?.gardu_induk || '...'}
            </h2>
            {trenLoading ? (
              <div className="flex justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              </div>
            ) : (
              <LineChartSVG data={trenSeries} dataKey={metricKey} color={metricColor[trenMetric]} />
            )}

            {!trenLoading && trenSeries.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                <div className="border border-gray-100 rounded-lg p-3">
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Nilai Terakhir</p>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold text-gray-800">{lastPoint ? lastPoint[metricKey].toFixed(1) : '-'}</span>
                    {delta !== null && (
                      <span
                        className="text-xs font-bold px-2 py-0.5 rounded-full"
                        style={{
                          backgroundColor: delta >= 0 ? '#00A65118' : '#D9362B18',
                          color: delta >= 0 ? '#00A651' : '#D9362B',
                        }}
                      >
                        {delta >= 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-gray-400 mt-1">{lastPoint?.label}</p>
                </div>
                <div className="border border-gray-100 rounded-lg p-3">
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Rata-rata Periode</p>
                  <span className="text-lg font-bold text-gray-800">{avgValue !== null ? avgValue.toFixed(1) : '-'}</span>
                </div>
                <div className="border border-gray-100 rounded-lg p-3">
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Titik Terendah</p>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold text-gray-800">{lowestPoint ? lowestPoint[metricKey].toFixed(1) : '-'}</span>
                    <span className="text-xs text-gray-400">{lowestPoint?.label}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h2 className="text-xs font-bold text-green-800 tracking-wide mb-3">PERBANDINGAN ANTAR GI (KOMPOSIT)</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {trenAllGI.map(gi => {
                const series = gi.tren || [];
                const last = series[series.length - 1];
                const prev = series[series.length - 2];
                const d = last && prev ? (last.nilai_psri - prev.nilai_psri) : null;
                return (
                  <div key={gi.id} className="border border-gray-100 rounded-lg p-3">
                    <div className="flex justify-between items-baseline mb-1">
                      <p className="text-xs font-bold text-gray-700">{gi.nama}</p>
                      {d !== null && (
                        <span className="text-[11px] font-bold" style={{ color: d >= 0 ? '#00A651' : '#D9362B' }}>
                          {d >= 0 ? '▲' : '▼'} {Math.abs(d).toFixed(1)}
                        </span>
                      )}
                    </div>
                    <MiniSparkline data={series} color="#00A651" />
                  </div>
                );
              })}
            </div>
          </div>

        </div>
      )}

      {modal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-6 z-50" onClick={() => setModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center px-5 py-4 border-b border-gray-100">
              <div>
                <h3 className="font-bold text-gray-800 text-sm">{modal.kategori.toUpperCase()} &mdash; {statusLabel[modal.status]}</h3>
                <p className="text-xs text-gray-400 mt-0.5">{modal.giNama} &middot; {modal.items.length} item</p>
              </div>
              <button onClick={() => setModal(null)} className="w-7 h-7 rounded-lg bg-gray-100 hover:bg-red-50 hover:text-red-600 text-gray-500 flex items-center justify-center">&#10005;</button>
            </div>
            <div className="p-5 overflow-y-auto flex-1">
              {modal.loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                </div>
              ) : modal.items.length === 0 ? (
                <p className="text-center text-gray-400 text-sm py-8">Tidak ada data</p>
              ) : (
                <div className="space-y-3">
                  {modal.items.map((item, i) => (
                    <div key={i} className="flex gap-3 pb-3 border-b border-gray-50 last:border-0">
                      {modal.kategori === 'apar' && (
                        <div className="w-14 h-14 min-w-[56px] rounded-lg bg-gray-50 border border-dashed border-gray-200 flex items-center justify-center text-xl overflow-hidden">
                          {item.foto ? <img src={item.foto} alt="" className="w-full h-full object-cover" /> : '📷'}
                        </div>
                      )}
                      <div>
                        <p className="font-semibold text-gray-800 text-sm">{item.kode}</p>
                        {item.lokasi && <p className="text-xs text-gray-400">{item.lokasi}</p>}
                        <span
                          className="text-xs font-medium mt-1 inline-block px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: `${statusColor[modal.status]}18`, color: statusColor[modal.status] }}
                        >
                          {item.alasan}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
